import { MCP_PERMISSION, noAccessReason } from '@dawn/feature-config';
import {
  Banner,
  Button,
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
  Spinner,
} from '@dawn/ui';
import { useId, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';

import { SessionExpiredModal } from '@/components/SessionExpiredModal';
import { useMcpOAuthDecision } from '@/hooks/mutations/use-mcp-oauth-decision';
import { useMcpOAuthClientQuery } from '@/hooks/queries/use-mcp-oauth-client-query';
import { usePermissions } from '@/hooks/use-permissions';
import { useSessionStore } from '@/stores/session';

/**
 * MCP OAuth consent (TDEV-1199). The API's authorization endpoint validated
 * the request and redirected here with its query intact; this page shows who
 * is asking, as whom and where the browser returns, then posts the same
 * parameters back with the answer. The API answers with the client's redirect
 * URI (built only from the validated request), and the browser leaves for it.
 * The page is behind ProtectedRoute: an unauthenticated visit goes through
 * /login and comes back here with the query intact.
 *
 * Allow needs MCP_PERMISSION (TDEV-1234): without it the page still shows the
 * request, disables Allow and says who to ask, and Deny still answers the client.
 * A courtesy — the API refuses the approval itself (403), which re-reads the
 * session user so a stale page catches up.
 */
export function McpAuthorizePage() {
  const [searchParams] = useSearchParams();
  const params = useMemo(() => Object.fromEntries(searchParams), [searchParams]);
  const { user } = useSessionStore();
  const { can } = usePermissions();
  const canConnect = can(MCP_PERMISSION);
  const noAccessId = useId();
  const client = useMcpOAuthClientQuery(params);
  const decision = useMcpOAuthDecision();

  const decide = (approved: boolean) => {
    decision.mutate(
      { approved, params },
      { onSuccess: ({ redirectTo }) => window.location.assign(redirectTo) }
    );
  };

  // Pending until the browser has actually left.
  const busy = decision.isPending || decision.isSuccess;

  return (
    <div className="flex min-h-screen items-center justify-center bg-muted p-4">
      <Card className="w-full max-w-md rounded-xl" data-testid="mcp-authorize-page">
        {client.isLoading ? (
          <CardContent
            className="flex items-center justify-center py-12"
            data-testid="mcp-authorize-loading"
          >
            <Spinner animate size={32} />
          </CardContent>
        ) : client.isError || !client.data ? (
          <CardContent className="pt-6">
            <Banner
              variant="error"
              className="rounded-md border"
              data-testid="mcp-authorize-invalid"
            >
              This connection request is invalid or has expired. Start the connection again from
              your MCP client.
            </Banner>
          </CardContent>
        ) : (
          <>
            <CardHeader>
              <CardTitle>Connect {client.data.clientName} to Dawn</CardTitle>
              <CardDescription>
                Allow <span className="font-medium text-foreground">{client.data.clientName}</span>{' '}
                to use Dawn as{' '}
                <span className="font-medium text-foreground" data-testid="mcp-authorize-email">
                  {user?.email}
                </span>
                ? It will return to{' '}
                <span className="font-medium text-foreground" data-testid="mcp-authorize-host">
                  {client.data.redirectHost}
                </span>
                .
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-3">
              <p className="text-sm text-muted-foreground" data-testid="mcp-authorize-scope">
                It will be able to read your organization&apos;s access data, as you can in the
                dashboard. You can disconnect it at any time in Settings → MCP.
              </p>
              {!canConnect && (
                <Banner
                  id={noAccessId}
                  variant="warning"
                  className="rounded-md border"
                  data-testid="mcp-authorize-no-access"
                >
                  You can&apos;t connect an MCP client yet. {noAccessReason(MCP_PERMISSION, can)}
                </Banner>
              )}
              {client.data.isLoopback && (
                <Banner
                  variant="warning"
                  className="rounded-md border"
                  data-testid="mcp-authorize-loopback-warning"
                >
                  This app returns to a program on your own computer. Continue only if you just
                  started this connection from that computer.
                </Banner>
              )}
              {decision.isError && (
                <Banner
                  variant="error"
                  className="rounded-md border"
                  data-testid="mcp-authorize-decision-error"
                >
                  Couldn&apos;t complete the connection. Start it again from your MCP client.
                </Banner>
              )}
            </CardContent>
            <CardFooter className="flex justify-end gap-2">
              <Button
                variant="outline"
                onClick={() => decide(false)}
                disabled={busy}
                data-testid="mcp-authorize-deny"
              >
                Deny
              </Button>
              <Button
                onClick={() => decide(true)}
                disabled={busy || !canConnect}
                aria-describedby={canConnect ? undefined : noAccessId}
                data-testid="mcp-authorize-allow"
              >
                Allow
              </Button>
            </CardFooter>
          </>
        )}
      </Card>
      {/* Outside DashboardLayout, so the expiry prompt is mounted here: its
          "Log in" keeps pathname + search, and consent resumes after sign-in. */}
      <SessionExpiredModal />
    </div>
  );
}
